import { useCheckEmailExistsLazyQuery, useCreateAdminUserMutation } from '@codezo/graphql';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export function InviteUserModal({
	open,
	onOpenChange,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}) {
	const [email, setEmail] = useState('');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [emailError, setEmailError] = useState<string | null>(null);

	const [checkEmailExists] = useCheckEmailExistsLazyQuery();
	const [createAdminUser, { loading }] = useCreateAdminUserMutation();

	const resetForm = () => {
		setEmail('');
		setFirstName('');
		setLastName('');
		setEmailError(null);
	};

	const handleSubmit = async () => {
		if (!email.trim() || !firstName.trim() || !lastName.trim()) return;

		if (!/^\S+@\S+$/.test(email.trim())) {
			setEmailError('Invalid email');
			return;
		}

		try {
			const emailCheck = await checkEmailExists({ variables: { email: email.trim() } });
			if (emailCheck.data?.checkEmailExists?.exists) {
				setEmailError('An account with this email already exists');
				return;
			}

			const { data } = await createAdminUser({
				variables: {
					input: {
						email: email.trim(),
						firstName: firstName.trim(),
						lastName: lastName.trim(),
					},
				},
			});

			if (data?.createAdminUser?.success) {
				toast.success('Invitation sent');
				resetForm();
				onOpenChange(false);
				onSuccess?.();
			} else {
				toast.error(data?.createAdminUser?.message || 'Failed to invite user');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to invite user');
		}
	};

	const canSubmit = email.trim() && firstName.trim() && lastName.trim();

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) resetForm();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Invite user</DialogTitle>
					<DialogDescription>Send an invitation to join as an admin.</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1.5">
									<Label>
										First name <span className="text-destructive">*</span>
									</Label>
									<Input
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										placeholder="John"
									/>
								</div>
								<div className="flex flex-col gap-1.5">
									<Label>
										Last name <span className="text-destructive">*</span>
									</Label>
									<Input
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
										placeholder="Doe"
									/>
								</div>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>
									Email <span className="text-destructive">*</span>
								</Label>
								<Input
									type="email"
									value={email}
									onChange={(e) => {
										setEmail(e.target.value);
										if (emailError) setEmailError(null);
									}}
									placeholder="user@example.com"
								/>
								{emailError && (
									<p className="text-xs text-destructive">{emailError}</p>
								)}
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								resetForm();
								onOpenChange(false);
							}}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit || loading}>
							{loading ? 'Sending…' : 'Send invitation'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
